import React from "react";
import styled from "styled-components";

import { ExpressionEditorValueFirst } from "../../../CommonUI/DataEditing/ExpressionEditorValueFirst";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { ExpressionConfiguration } from "../../../rule-engine-core/Expressions/ExpressionConfiguration";
import {
    IntersectionResult,
    IntersectionRuleConfiguration,
} from "../../../rule-engine-core/Rule/Intersection/IntersectionRuleConfiguration";
import { Error } from "../../Table/CommonUI/Error";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { ExpressionView } from "../../../Expressions/ExpressionView";
import { TestingConstants } from "../../../CommonUI/Testing/TestingConstants";
import { ExpressionTestResultColorContext } from "../../../Expressions/Testing/ExpressionTestResultColorContext";
import {
    ExpressionReactContext,
    ExpressionRenderType,
    expressionSettings,
} from "../../../Expressions/Structure/ExpressionReactContext";
import { PassedTestRow } from "../../Table/CommonStyles";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

interface IntersectionRuleResultProps {
    ruleDefinition: IntersectionRuleConfiguration;
    resultCell: IntersectionResult;
    x: number;
    y: number;
}

export const IntersectionRuleResult = ({ resultCell, ruleDefinition, x, y }: IntersectionRuleResultProps) => {
    const testContext = React.useContext(TestContext);

    const onUpdate = (newValue: ExpressionConfiguration | any) => {
        resultCell.setValue(newValue);
        StateManager.changed(ruleDefinition);
        StateManager.propertyChanged(resultCell, "resultExpression");
    };

    if (testContext) {
        const issues = resultCell.getDesignValidationIssues();

        const resulttestitem = testContext.findItem(resultCell.resultExpression._id);

        let testcolor: string | undefined;
        let borderColor: string | undefined;
        let backgroundColor: string | undefined;

        if (resulttestitem) {
            testcolor = TestingConstants.TEXT_SUCCESS;
            borderColor = TestingConstants.BORDER_SUCCESS;
        }

        if (resulttestitem?.isError) {
            backgroundColor = TestingConstants.BACKGROUND_FAIL;
            borderColor = TestingConstants.BORDER_FAIL;
        }

        return (
            <ExpressionTestResultColorContext.Provider value={testcolor}>
                {!!resulttestitem && (
                    <PassedTestRow
                        x={1}
                        columnsSpan={x + 4}
                        y={y + 2}
                    />
                )}
                {!!resulttestitem && (
                    <PassedTestColumns
                        x={x + 3}
                        rowsSpan={y + 3}
                        y={1}
                    />
                )}
                <ExpressionReactContext.Provider value={expressionSettings[ExpressionRenderType.TEXT]}>
                    <ResultColumnRender
                        backgroundColor={backgroundColor}
                        borderColor={borderColor}
                        x={x + 3}
                        y={y + 2}
                    >
                        <ExpressionView
                            expression={resultCell.resultExpression}
                            hideValidation
                            color={{
                                expression: UIStyleConstants.BLUE_COLOR,
                                value: "" + UIStyleConstants.GRAY_LIGHTER_COLOR,
                                variable: UIStyleConstants.GRAY_DARK_COLOR,
                                missing: "" + UIStyleConstants.GRAY_LIGHTER_COLOR,
                            }}
                        />
                        <Error issues={issues} />
                    </ResultColumnRender>
                </ExpressionReactContext.Provider>
            </ExpressionTestResultColorContext.Provider>
        );
    }

    return (
        <ResultColumnRender>
            <ObserveState
                listenTo={resultCell}
                properties={["resultExpression"]}
                control={() => {
                    const issues = resultCell.getDesignValidationIssues();
                    return (
                        <>
                            <ExpressionEditorValueFirst
                                expression={resultCell.resultExpression}
                                onUpdate={onUpdate}
                                hideValidation
                            />
                            <Error issues={issues} />
                        </>
                    );
                }}
            />
        </ResultColumnRender>
    );
};

const ResultColumnRender = styled.div<{
    selected?: boolean;
    backgroundColor?: string;
    borderColor?: string;
    x?: number;
    y?: number;
}>`
    position: relative;

    background: ${(props) => props.backgroundColor || UIStyleConstants.WHITE_COLOR};
    border-radius: 2px;
    position: relative;

    color: ${UIStyleConstants.GRAY_LIGHT_COLOR};

    ${(props) =>
        props.selected
            ? `border: 1px solid ${props.borderColor || UIStyleConstants.BLUE_GRAY_COLOR}`
            : `border: 1px solid ${props.borderColor || "" + UIStyleConstants.GRAY_LIGHTER_COLOR}`};

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;

    ${(props) =>
        props.x !== undefined
            ? `
                grid-column-start: ${props.x + 1};
                grid-column-end: ${props.x + 2};
            `
            : ``}
    ${(props) =>
        props.y !== undefined
            ? `
                grid-row-start: ${props.y + 1};
                grid-row-end: ${props.y + 2};
            `
            : ``}
  .ValueEditor-root {
        width: 100%;
        padding: 6px 4px;

        .ValueEditor-display {
            padding: 6px 12px;
        }
    }
`;

const PassedTestColumns = styled.div<{ x: number; y?: number; rowsSpan: number }>`
    border: 2px solid ${TestingConstants.BORDER_SUCCESS};

    margin: -4px;

    grid-column-start: ${(props) => props.x + 1};
    grid-column-end: ${(props) => props.x + 2};

    grid-row-start: ${(props) => props.y || 1};
    grid-row-end: ${(props) => (props.y || 1) + props.rowsSpan};
`;
